// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {HeaderService} from 'src/app/header.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
    AuthMode,
    AuthModeName,
    BitLockerEncryption,
    getEncryptionStrengthFromEncryptionMethod,
    getEncryptionStrengthFromVolumeEncryptionType,
    getEncryptionTypeDisplayFromVolume,
    getFullOpalState,
    getPolicyEncryptionTypeDisplay,
    getProtectorDisplayValue,
    getVolumeEncryptionTypeDisplay,
    IConfigPbaSettings,
    IDevice,
    IDisk,
    IFdeSettings,
    IIntegration,
    IPBAInfo,
    IPendingList, ISubscription,
    IVolume,
    IVolumeProtection,
    MIN_AUTH_WIN_NATIVE_VERSION,
    MIN_BITLOCKER_VERSION,
    MIN_BITLOCKER_WINDOWS_VERSION,
    StartupAuthType, StripeProductTier,
    TPM_MIN_VERSION,
    VolumeProtection,
    VolumeStatus,
    VolumeType
} from 'src/types/subscription';
import {D3eService} from 'src/services/d3e-service';
import {BehaviorSubject, EMPTY, forkJoin, interval, Observable, of} from 'rxjs';
import {catchError, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {
    ID3EPolicy,
    IEffectivePolicyResponse,
    IFdeLicenseInfo,
    IForcePolicyResetRequest,
    IHomeConfig,
    IPbaLicenseInfo,
    PolicyCompliance,
    ProductUserRole, 
    ransomwareFeatureMaskValues,
    ConsoleAction,
    IPbaUser,
    IFdeUser,
    IFdeEnrollment,
    IPbaEnrollment
} from 'src/types/d3ePolicy';
import {NotificationService} from 'src/services/notification-service';
import { replaceGuidsWithMountLetters } from 'src/shared/guid-to-mount-letters';
import {PartnerTypes} from 'src/types/integrations';
import {LicenseMethods} from 'src/shared/license.methods';
import {Subscription as rxjsSubscription} from 'rxjs/internal/Subscription';
import {IDeviceActiveLock} from 'src/types/device-details';
import {IDrive, toDriveInfo} from '../../../types/drive';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import authHelper from '../../auth/authHelper';
import {RestConfigService} from '../../../services/config-service';
import {IRestConfig} from '../../../types/config';
import {IColumnChooserElement} from '../../shared/components/column-chooser/column-chooser.component';
import {BitLockerCompliance} from '../../../types/bitlocker-compliance';
import {LogService} from '../../../services/log.service';
import {Store} from '@ngrx/store';
import {AppState} from '../../store/app.state';
import {IManageUser, UserRole} from '../../../types/user';
import {selectUser} from '../../store/user/user.selectors';
import {MatDialog} from '@angular/material/dialog';
import {AlertDialogComponent} from '../../dialogs/alert/alert.component';
import { ConfigurationService } from 'src/services/configuration-service';
import * as Sentry from '@sentry/browser';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Enable the UTC plugin
dayjs.extend(utc);

const LOCK_STATE_UNLOCKED = 0;
const LOCK_STATE_UNLOCKED_AND_PENDING = 1;
const LOCK_STATE_LOCKED = 2;
const LOCK_STATE_LOCKED_AND_PENDING = 3;

export class BitLockerStatus {
    constructor(private readonly device: IDevice,
                private readonly policy: ID3EPolicy,
                private readonly translate: TranslateService) {}

    public readonly TPM_MIN_VERSION = TPM_MIN_VERSION;
    public readonly MIN_BITLOCKER_VERSION = MIN_BITLOCKER_VERSION;
    public readonly MIN_BITLOCKER_WINDOWS_VERSION = MIN_BITLOCKER_WINDOWS_VERSION;

    get tpm() {
        const tpm = this.device?.config?.tpm;
        if (!!tpm) {
            return tpm.split(',')[0];
        }
        return "0.0";
    }

    get ddCompatible() {
        return LicenseMethods.compareVersion(this.device?.version, this.MIN_BITLOCKER_VERSION) >= 0;
    }

    get osVersionCompatible() {
        return LicenseMethods.compareVersion(this.device?.config?.os_version, this.MIN_BITLOCKER_WINDOWS_VERSION) >= 0;
    }

    get tpmCompatible() {
        const version = this.device?.config?.tpm?.split(",")[0];
        if (!!version) {
            return LicenseMethods.compareVersion(version, this.TPM_MIN_VERSION) >= 0;
        }
        return false;
    }

    get deviceOSDriveEncryption(): string {
        return this.getEncryptionTypes(VolumeType.OPERATING_SYSTEM_VOLUME)?.join(", ");
    }

    get deviceFixedDriveEncryption(): string {
        return this.getEncryptionTypes(VolumeType.FIXED_DATA_VOLUME)?.join(", ");
    }

    get backupToMcCompliant() {
        if (this.policyBackupToMc) {
            return this.allVolumeKeysBackedUp;
        }
        return true;
    }

    get policyBackupToAd() {
        return this.policy?.bitlocker?.generalSettings?.backupKeyToActiveDirectory;
    }

    get backupToAd() {
        return this.getRegistryInt("ActiveDirectoryBackup") == 1;
    }

    get backupoAdCompliant() {
        if (this.policyBackupToAd) {
            return this.backupToAd;
        }
        return true;
    }

    get volumes(): IVolume[] {
        return this.device?.config?.disks?.reduce((acc, disk): IVolume[] => {
            return acc.concat(disk.volumes);
        }, []) ?? [];
    }

    getEncryptionTypes(volumeType: VolumeType): string[] {
        return this.volumes
          ?.filter(volume => volume?.volume_type === volumeType)
          .map((volume): string => getVolumeEncryptionTypeDisplay(volume.volume_encryption?.encryption_type || 0, this.translate)) || [];
    }

    get policyOSDriveEncryption(): string {
        if (this.policy?.bitlocker?.osDrive?.manageBitlockerEncryption === BitLockerEncryption.turnOnEncryption) {
            return getPolicyEncryptionTypeDisplay(this.policy?.bitlocker?.osDrive?.encryptionMethod, this.translate);
        }
        return this.translate.instant('bitlocker-status.not-required');
    }

    get policyFixedDriveEncryption(): string {
        if (this.policy?.bitlocker?.dataDrives?.manageBitlockerEncryption === BitLockerEncryption.turnOnEncryption) {
            return getPolicyEncryptionTypeDisplay(this.policy?.bitlocker?.dataDrives?.encryptionMethod, this.translate);
        }
        return this.translate.instant('bitlocker-status.not-required');
    }

    get policyBackupToMc(): boolean {
        return this.policy?.bitlocker?.generalSettings?.backupKeyToManagementConsole;
    }

    get osVolumes(): IVolume[] {
        return this.volumes?.filter(v => {
            return v?.volume_type === VolumeType.OPERATING_SYSTEM_VOLUME;
        }) ?? [];
    }

    get fixedVolumes(): IVolume[] {
        return this.volumes?.filter(v => {
            return v?.volume_type === VolumeType.FIXED_DATA_VOLUME;
        }) ?? [];
    }

    get haveNoncompliantOsVolumes() {
        if (this.policy?.bitlocker?.osDrive?.manageBitlockerEncryption === BitLockerEncryption.turnOnEncryption) {
            return this.getNonCompliantVolumes(this.osVolumes).length === 0;
        }
        return true;
    }

    get haveNoncompliantFixedVolumes() {
        if (this.policy?.bitlocker?.dataDrives?.manageBitlockerEncryption === BitLockerEncryption.turnOnEncryption) {
            return this.getNonCompliantVolumes(this.fixedVolumes).length === 0;
        }
        return true;
    }

    getNonCompliantVolumes(volumes: IVolume[]): IVolume[] {
        return volumes?.filter(v => {
            const volumeStrength = getEncryptionStrengthFromVolumeEncryptionType(v.volume_encryption?.encryption_type);
            const policyStrength = getEncryptionStrengthFromEncryptionMethod(this.policy.bitlocker?.osDrive?.encryptionMethod);
            return volumeStrength < policyStrength;
        }) ?? [];
    }

    get allVolumeKeysBackedUp(): boolean {
        // filter out volumes that are backed up or don't need to be encrypted
        if (this.volumes == undefined) {
            return true;
        }
        return this.volumes?.filter(v => {
            if (v?.filesystem?.toLowerCase() != 'ntfs') {
                return false;
            }
            if(v?.volume_type == VolumeType.REMOVABLE_DATA_VOLUME) {  // filter out removable drives
                return false;
            }
            if (v?.volume_type == undefined) {
                return false;
            }
            if (!this.policy?.bitlocker?.generalSettings?.backupKeyToManagementConsole) {
                return false;
            }
            if (v?.volume_type === VolumeType.OPERATING_SYSTEM_VOLUME &&
              this.policy.bitlocker.osDrive.manageBitlockerEncryption != BitLockerEncryption.turnOnEncryption) {
                return false;
            }
            if (v?.volume_type === VolumeType.FIXED_DATA_VOLUME &&
              this.policy.bitlocker.dataDrives.manageBitlockerEncryption != BitLockerEncryption.turnOnEncryption) {
                return false;
            }
            return this.device.volume_config_guids.indexOf(v.guid.replace(/(^\{|\}$)/g, "")) <= -1;
          }
        ).length === 0;
    }

    getRegistryInt(name: string): number {
        const reg = this.device?.config?.hklm_software_policies_microsoft_fve || {};
        const dword = reg[name];
        if (!dword) {
            return 0;
        }
        const [label, intString] = dword.split(':', 2);
        if (label !== 'dword') {
            throw new Error("invalid dword string");
        }
        return parseInt(intString, 16);
    }

    getStartupTypeDisplay(required: boolean, startupAuthType: StartupAuthType | -1): string {
        if (!required) {
            return this.translate.instant('bitlocker-status.not-required');
        }
        switch(startupAuthType) {
            case StartupAuthType.tpmOnly:
                return this.translate.instant('policy.bitlocker.os-drive.require-startup-auth-tpm-only');
            case StartupAuthType.tpmWithPIN:
                return this.translate.instant('policy.bitlocker.os-drive.require-startup-auth-tpm-with-pin');
            case StartupAuthType.tpmWithPINAndStartup:
                return this.translate.instant('policy.bitlocker.os-drive.require-startup-auth-tpm-with-pin-and-startup-key');
            case StartupAuthType.tpmWithStartupKey:
                return this.translate.instant('policy.bitlocker.os-drive.require-startup-auth-tpm-with-startup-key');
        }
        return this.translate.instant('NOT_SET');
    }

    get policyStartupTypeDisplay(): string {
        return this.getStartupTypeDisplay(
          this.policy?.bitlocker?.osDrive?.requireStartupAuth,
          this.policy.bitlocker?.osDrive?.startupAuthType);
    }

    get startupTypeDisplay(): string {
        return this.getStartupTypeDisplay(true, this.startupType);
    }

    get startupType(): StartupAuthType | -1 {
        if (this.getRegistryInt("UseTPM")) {
            return StartupAuthType.tpmOnly;
        } else if (this.getRegistryInt("UseTPMPIN")) {
            return StartupAuthType.tpmWithPIN;
        } else if (this.getRegistryInt("UseTPMKey")) {
            return StartupAuthType.tpmWithStartupKey;
        } else if (this.getRegistryInt("UseTPMKeyPIN")) {
            return StartupAuthType.tpmWithPINAndStartup;
        }
        return -1;
    }

    get startupTypeCompliant(): boolean {
        if (this.policy?.bitlocker?.osDrive?.requireStartupAuth) {
            if (this.policy.bitlocker?.osDrive?.startupAuthType == undefined) {
                return false;
            }
            return this.policy.bitlocker?.osDrive?.startupAuthType === this.startupType;
        }
        return true;
    }
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-d3e-device',
    templateUrl: './d3e-device.component.html',
    styleUrls: ['./d3e-device.component.scss'],
    standalone: false
})
export class D3eDeviceComponent implements OnInit, OnDestroy {
    private interval$: BehaviorSubject<number> = new BehaviorSubject(10);
    user$: Observable<IManageUser> = this.store.select(selectUser());
    SUPPORT_NONE = 0x00n;
    SUPPORT_KEEPALIVE = 0x00000001n;
    SUPPORT_ERASE_VERIFY = 0x00000002n;
    SUPPORT_CMD_LOGGING = 0x00000004n;
    SUPPORT_CONFIG_CMDLOG = 0x00000008n;
    ENABLED_KEEPALIVE = 0x00010000n;
    ENABLED_CMD_LOGGING = 0x00040000n;
    ENABLED_RANSOMWARE = 0x0800000n;
    ACTIVE_RANSOMWARE = 0x80000000n;
    SUPPORTS_SMCU = 0x000100000000n; // if supports smcu, then supports ransomware

    authModesDisplayOrder: AuthMode[] = [
        AuthMode.AUTH_WIN_NATIVE,
        AuthMode.AUTH_PIN,
        AuthMode.AUTH_GOOGLE,
        AuthMode.AUTH_FINGER_DEPRECATED,
        AuthMode.AUTH_FACE_DEPRECATED,
        AuthMode.AUTH_DUO,
        AuthMode.AUTH_PIV
    ];
    authModesDisplayOrderFiltered = this.authModesDisplayOrder;

    diskColumns: string[] = ['model', 'serial',  'size', 'type', 'secure', 'volumes', 'firmware', 'opal_state', 'pba_installed',
        'tcg_support', 'keepalive_enabled', 'cmd_logging_enabled'];
    authColumns: string[] = ['method', 'active', 'configured'];
    partitionColumns: IColumnChooserElement[] = [];
    partitionColumnsToDisplay: string[];

    device: IDevice = null;
    device$: BehaviorSubject<IDevice> = new BehaviorSubject<IDevice>(null);
    volumes: IVolume[] = [];
    pbaInfo: IPBAInfo;
    effectivePolicy: IEffectivePolicyResponse;
    lastRefresh: Date;
    refreshTimeSkeletonTheme = {
        display: 'flex',
        height: '20px',
        width: '100%',
        margin: '0',
        padding: '0'
    };

    filePluralMapping: { [k: string]: string } = {
      '=0': 'All Files',
      '=1': '1 File',
      'other': '# Files'
    };

    show_force_sync_btn: boolean;
    forcePolicyResetEnabled: boolean;

    consoleLockState = 0;
    deviceActiveLocks: IDeviceActiveLock[] = [];
    // FROM Device Page
    disconnect_threshold = 900;
    sensorsById = [];
    refreshSeconds = 60;
    refreshPendingSeconds = 5;
    deviceSets = [];
    deviceSetsEnabled = false;
    selectedSetId = null;
    selectedSet = null;
    integrations: IIntegration[] = [];
    configuredIntegrations: number[] = [];

    destroyed = false;
    private timerSubscription: rxjsSubscription;
    boundVisibilityHandler: any;
    homeConfig: IHomeConfig;

    // Features Easter Egg
    showFeatures = false;
    ardbeg = ['KeyA', 'KeyR', 'KeyD', 'KeyB', 'KeyE', 'KeyG'];
    ardbegBuffer = [];
    THIRD_PARTY_THREAT_IDS = [...this.range(99, 1501)];
    ALL_INTEGRATION_THREAT_IDS = [1002, 1003, 1004, 1005, 1006, 1007, 1008, 1009, 1010, 1014, ...this.THIRD_PARTY_THREAT_IDS];
    integrationMetadata: IIntegration[] = [
        {
            partnerId: -1,
            name: 'Any',  // Translated and set below
            threatIds: this.ALL_INTEGRATION_THREAT_IDS
        },
        {
            partnerId: PartnerTypes.THIRD_PARTY,
            name: 'Third Party',
            threatIds: this.THIRD_PARTY_THREAT_IDS
        },
        {
            partnerId: PartnerTypes.CARBON_BLACK,
            name: 'Carbon Black',
            threatIds: [1002, 1003, 1004]
        },
        {
            partnerId: PartnerTypes.CISCO_AMP,
            name: 'Cisco',
            threatIds: [1009, 1010]
        },
        {
            partnerId: PartnerTypes.CYBERARK_EPM,
            name: 'Cyber Ark',
            threatIds: [1005, 1006]
        },
        {
            partnerId: PartnerTypes.SOPHOS,
            name: 'SOPHOS',
            threatIds: [1007, 1008]
        },
        {
            partnerId: PartnerTypes.CIGENT_TESTING,
            name: 'Test Integration',
            threatIds: [1014, 1015]
        },

    ];

    bitLockerStatus$: BehaviorSubject<BitLockerStatus> = new BehaviorSubject<BitLockerStatus>(null);
    public readonly BitlockerCompliance = BitLockerCompliance;

    subscriptions$ = this.headerService.orgSelectedChanged.pipe(
      switchMap(orgId  => this.d3eService.get_subscriptions(orgId)),
      map(subscriptions => {
          const utcnow = dayjs.utc();
          return subscriptions.filter((subscription: ISubscription) => {
              const valid = dayjs.utc(subscription.exp_timestamp) > utcnow &&
                dayjs.utc(subscription.sub_timestamp) < utcnow;
              return subscription.count > 0 && valid;
          });
      })
    );

    haveDataDefenseSubscription$ = this.subscriptions$.pipe(
      map(subscriptions => subscriptions.filter(
        s => s.stripe_product.tier == StripeProductTier.BUSINESS ||
          s.stripe_product.tier == StripeProductTier.PERSONAL).length > 0
      )
    );

    haveBitlockerSubscription$ = this.subscriptions$.pipe(
      map(subscriptions => subscriptions.filter(
        s => s.stripe_product.tier == StripeProductTier.BUSINESS ||
          s.stripe_product.tier == StripeProductTier.PERSONAL ||
          s.stripe_product.tier == StripeProductTier.BITLOCKER).length > 0
      )
    );

    networkSensorEnabled = true;

    CONSOLE_MANUAL_LOCK = 1001;
    selectedTab = 0;

    fLastSeen: string;
    fConnected: string;
    fStatus: string;
    fDetails: string;
    fManualLock: string;
    fCaseA: string;
    fCaseB: string;
    fCaseC: string;
    fCaseD: string;
    fCaseE: string;
    fLastActivity: string;
    fActiveLock: string;
    fEngaged: string;
    fStandingGuard: string;
    fActiveThreat: string;
    fActivated: string;
    fFeatureStatus: string;
    fSecureDrives: string;
    fConfigured: string;
    fCredsRequired: string;
    fUnconfigured: string;
    fFileProt: string;
    fEnabled: string;
    fDisabled: string;
    fFolderProt: string;
    fDecepFiles: string;
    fNetDeception: string;
    fOverview: string;
    fGeneral: string;
    fHost: string;
    fVersion: string;
    fLastContact: string;
    fNever: string;
    fSystem: string;
    fManufacturer: string;
    fModel: string;
    fSysSerial: string;
    fOSSerial: string;
    fNotAvail: string;
    fDisks: string;
    fMethod: string;
    fSize: string;
    fType: string;
    fVolumes: string;
    fFirmware: string;
    fKeepAlive: string;
    fCommandLogging: string;
    fActiveLockConfig: string;
    fNetworks: string;
    fNetDetails: string;
    fOnline: string;
    fOffline: string;
    fName: string;
    fTrusted: string;
    fFirstCon: string;
    fIPv4: string;
    fMac: string;
    fMask: string;
    fAuthentication: string;
    fAuthDetail: string;
    fDefault: string;
    fConfig: string;
    fYes: string;
    fNo: string;
    fInvalid: string;
    fFingerprint: string;
    fFace: string;
    fAuthApp: string;
    fPinDescription: string;
    fAuthAppDescription: string;
    fFingerprintDescription: string;
    fFaceDescription: string;
    fInvalidDescription: string;
    fSynchSuccess: string;
    fErrorSyncing: string;
    fAny: string;
    fUnknownThreat: string;
    fManualLockNoDash: string;
    fAvailable: string;
    fna: string;
    fMillisecond: string;
    fSecond: string;
    fMinute: string;
    fHour: string;
    fDay: string;
    fWeek: string;

    title: string;
    showRansomware: boolean;
    restConfig: IRestConfig;
    showOnlyMounted = true;
    clearing: boolean;
    hasPbaSettings: boolean = false;
    hasFde: boolean = false;
    CIGENT_FDE = 'CigentFDE';
    pbaSettings: null | IConfigPbaSettings = null;
    fdeSettings: null | IFdeSettings = null;

    constructor(
        public translate: TranslateService,
        public readonly configurationService: ConfigurationService,
        private route: ActivatedRoute,
        private d3eService: D3eService,
        private headerService: HeaderService,
        private notification: NotificationService,
        private router: Router,
        private readonly restConfigService: RestConfigService,
        private readonly logService: LogService,
        private cdr: ChangeDetectorRef,
        private readonly dialog: MatDialog,
        private readonly store: Store<AppState>,
    ) {
        this.translate.get(['D3ED_LAST_SEEN', 'D3ED_CONNECTED', 'D3ED_STATUS', 'D3ED_DETAIL', 'D3ED_MANLOCK', 'D3ED_CASEA',
            'D3ED_CASEB', 'D3ED_CASEC', 'D3ED_CASED', 'D3ED_CASEE', 'D3ED_LAST_ACTIVITY', 'D3ED_ACTIVELOCK', 'D3ED_ENGAGED',
            'D3ED_STANDING_GUARD', 'D3ED_ACTIVETHREAT', 'D3ED_ACTIVATED', 'D3ED_FEATURE_STATUS', 'D3ED_SECURE_DRIVE', 'D3ED_CONFIGURED',
            'D3ED_UNCONFIGURED', 'D3ED_FILE_PROT', 'D3ED_ENABLED', 'D3ED_DISABLED', 'D3ED_FOLDER_PROT', 'D3ED_DECEP_FIlES',
            'D3ED_NET_DECEP', 'D3ED_OVERVIEW', 'D3ED_GENERAL', 'D3ED_HOST', 'D3ED_VERSION', 'D3ED_LAST_CONTACT',
            'D3ED_NEVER', 'D3ED_SYS', 'MANUFACTURER', 'MODEL', 'SYSTEM_SERIAL', 'D3ED_OS_SERIAL', 'D3ED_NOT_AVAIL', 'D3ED_DISKS',
            'D3ED_METHOD', 'D3ED_SIZE', 'D3ED_TYPE', 'D3ED_VOLUME', 'D3ED_FIRMWARE', 'D3ED_KEEP_ALIVE', 'D3ED_COMMAND',
            'D3ED_ACTIVE_CONFIG', 'D3ED_NET', 'D3ED_NET_DETAILS', 'D3ED_ONLINE', 'D3ED_OFFLINE', 'NAME', 'D3ED_TRUSTED',
            'D3ED_FIRST_CON', 'D3ED_IPV4', 'D3ED_MAC', 'D3ED_MASK', 'D3ED_AUTH', 'D3ED_AUTH_DETAIL', 'D3ED_DEFAULT', 'D3ED_CONFIG',
            'TITLE_ENDPOINT_DETAIL', 'YES_NORMAL_CAP', 'NO_NORMAL_CAP', 'INVALID', 'FINGERPRINT', 'FACE_RECOGNITION', 'CISCO_DUO', 'AUTH_APP', 'AMD_PIN',
            'AMD_AUTH_APP', 'AMD_FINGERPRINT', 'AMD_FACE_RECOGNITION', 'AMD_CISCO_DUO', 'AMD_PIV', 'AMD_INVALID',
            'D3ED_ERROR_SYNCING', 'D3ED_SYNC_SUCCESS', 'ANY', 'D3ED_UNKNOWN_THREAT', 'MANUAL_LOCK', 'D3ED_AVAILABLE',
            'N_A', 'CREDS_REQUIRED',  'MILLISECOND_LC', 'SECOND_LC', 'MINUTE_LC', 'HOUR_LC',
            'DAY_LC', 'WEEK_LC', 'NEVER'])
            .subscribe(vals => {
            this.fLastSeen = vals['D3ED_LAST_SEEN'];
            this.fConnected = vals['D3ED_CONNECTED'];
            this.fStatus = vals['D3ED_STATUS'];
            this.fDetails = vals['D3ED_DETAIL'];
            this.fManualLock = vals['D3ED_MANLOCK'];
            this.fCaseA = vals['D3ED_CASEA'];
            this.fCaseB = vals['D3ED_CASEB'];
            this.fCaseC = vals['D3ED_CASEC'];
            this.fCaseD = vals['D3ED_CASED'];
            this.fCaseE = vals['D3ED_CASEE'];
            this.fLastActivity = vals['D3ED_LAST_ACTIVITY'];
            this.fActiveLock = vals['D3ED_ACTIVELOCK'];
            this.fStandingGuard = vals['D3ED_STANDING_GUARD'];
            this.fEngaged = vals['D3ED_ENGAGED'];
            this.fActiveThreat = vals['D3ED_ACTIVETHREAT'];
            this.fActivated = vals['D3ED_ACTIVATED'];
            this.fFeatureStatus = vals['D3ED_FEATURE_STATUS'];
            this.fSecureDrives = vals['D3ED_SECURE_DRIVE'];
            this.fConfigured = vals['D3ED_CONFIGURED'];
            this.fUnconfigured = vals['D3ED_UNCONFIGURED'];
            this.fFileProt = vals['D3ED_FILE_PROT'];
            this.fEnabled = vals['D3ED_ENABLED'];
            this.fDisabled = vals['D3ED_DISABLED'];
            this.fFolderProt = vals['D3ED_FOLDER_PROT'];
            this.fDecepFiles = vals['D3ED_DECEP_FIlES'];
            this.fNetDeception = vals['D3ED_NET_DECEP'];
            this.fOverview = vals['D3ED_OVERVIEW'];
            this.fGeneral = vals['D3ED_GENERAL'];
            this.fHost = vals['D3ED_HOST'];
            this.fVersion = vals['D3ED_VERSION'];
            this.fLastContact = vals['D3ED_LAST_CONTACT'];
            this.fNever = vals['D3ED_NEVER'];
            this.fSystem = vals['D3ED_SYS'];
            this.fManufacturer = vals['MANUFACTURER'];
            this.fModel = vals['MODEL'];
            this.fSysSerial = vals['SYSTEM_SERIAL'];
            this.fOSSerial = vals['D3ED_OS_SERIAL'];
            this.fNotAvail = vals['D3ED_NOT_AVAIL'];
            this.fDisks = vals['D3ED_DISKS'];
            this.fMethod = vals['D3ED_METHOD'];
            this.fSize = vals['D3ED_SIZE'];
            this.fType = vals['D3ED_TYPE'];
            this.fVolumes = vals['D3ED_VOLUME'];
            this.fFirmware = vals['D3ED_FIRMWARE'];
            this.fKeepAlive = vals['D3ED_KEEP_ALIVE'];
            this.fCommandLogging = vals['D3ED_COMMAND'];
            this.fActiveLockConfig = vals['D3ED_ACTIVE_CONFIG'];
            this.fNetworks = vals['D3ED_NET'];
            this.fNetDetails = vals['D3ED_NET_DETAILS'];
            this.fOnline = vals['D3ED_ONLINE'];
            this.fOffline = vals['D3ED_OFFLINE'];
            this.fName = vals['NAME'];
            this.fTrusted = vals['D3ED_TRUSTED'];
            this.fFirstCon = vals['D3ED_FIRST_CON'];
            this.fIPv4 = vals['D3ED_IPV4'];
            this.fMac = vals['D3ED_MAC'];
            this.fMask = vals['D3ED_MASK'];
            this.fAuthentication = vals['D3ED_AUTH'];
            this.fAuthDetail = vals['D3ED_AUTH_DETAIL'];
            this.fDefault = vals['D3ED_DEFAULT'];
            this.fConfig = vals['D3ED_CONFIG'];
            this.fYes = vals['YES_NORMAL_CAP'];
            this.fNo = vals['NO_NORMAL_CAP'];
            this.fInvalid = vals['INVALID'];
            this.fFingerprint = vals['FINGERPRINT'];
            this.fFace = vals['FACE_RECOGNITION'];
            this.fAuthApp = vals['AUTH_APP'];
            this.fPinDescription = vals['AMD_PIN'];
            this.fAuthAppDescription = vals['AMD_AUTH_APP'];
            this.fFingerprintDescription = vals['AMD_FINGERPRINT'];
            this.fFaceDescription = vals['AMD_FACE_RECOGNITION'];
            this.fInvalidDescription = vals['AMD_INVALID'];
            this.fSynchSuccess = vals['D3ED_SYNC_SUCCESS'];
            this.fErrorSyncing = vals['D3ED_ERROR_SYNCING'];
            this.title = vals['TITLE_ENDPOINT_DETAIL'];
            this.fAny = vals['ANY'];
            this.fUnknownThreat = vals['D3ED_UNKNOWN_THREAT'];
            this.fManualLockNoDash = vals['MANUAL_LOCK'];
            this.fAvailable = vals['D3ED_AVAILABLE'];
            this.fna = vals['N_A'];
            this.fCredsRequired = vals['CREDS_REQUIRED'];
            this.fMillisecond = vals['MILLISECOND_LC'];
            this.fSecond = vals['SECOND_LC'];
            this.fMinute = vals['MINUTE_LC'];
            this.fHour = vals['HOUR_LC'];
            this.fDay = vals['DAY_LC'];
            this.fWeek = vals['WEEK_LC'];
            this.fNever = vals['NEVER'];

            this.integrationMetadata[0].name = this.fAny;
        });
        this.headerService.setTitle(this.title);
        this.headerService.setSubtitle('');
        this.headerService.setIcon('mdi-monitor');
        this.headerService.showUserFilter(false);
        this.headerService.showRefreshButton(true);

        this.headerService.refresh$.pipe(untilDestroyed(this)).subscribe(refresh => {
            if (refresh && !!this.device?.uuid) {
                this.refreshHomeDevice(this.device.uuid);
            }
        });

        this.partitionColumns = [
            {title: this.translate.instant('endpoints.partitions.guid'), value: 'guid', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.label'), value: 'label', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.mount'), value: 'mount', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.disk_serial'), value: 'disk_serial', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.volume_type'), value: 'volume_type', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.filesystem'), value: 'filesystem', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.size'), value: 'size', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.locked'), value: 'locked', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.protection'), value: 'protection', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.protectors'), value: 'protectors', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.encryption_type'), value: 'encryption_type', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.encryption_flags'), value: 'encryption_flags', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.status'), value: 'status', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.partitions.percent_encrypted'), value: 'percent_encrypted', selected: true, hidden: false,},
        ];

        this.route.queryParamMap.subscribe(params => {
            const tab = params.get('tab');
            switch (tab) {
                case 'status':
                    this.selectedTab = 0;
                    break;
                case 'details':
                    this.selectedTab = 1;
                    break;
                case 'protections':
                    this.selectedTab = 2;
                    break;
                case 'bitlocker':
                    this.selectedTab = 3;
                    break;
                case 'sensors':
                    this.selectedTab = 4;
                    break;
                case 'authentication':
                    this.selectedTab = 5;
                    break;
                case 'settings':
                    this.selectedTab = 6;
                    break;
            }
        });

        this.bitLockerStatus$.pipe(
          withLatestFrom(this.device$),
        ).subscribe(([bitlockerStatus, device]: [BitLockerStatus, IDevice]) => {
            if (!bitlockerStatus) {
                return EMPTY;
            }
            const messages = [
                {
                    message: "Data Defense version incompatible",
                    compliant: bitlockerStatus.ddCompatible,
                },
                {
                    message: "OS Version incompatible",
                    compliant: bitlockerStatus.osVersionCompatible,
                },
                {
                    message: "TPM is incompatible",
                    compliant: bitlockerStatus.tpmCompatible,
                },
                {
                    message: "Has non-compliant OS volumes",
                    compliant: bitlockerStatus.haveNoncompliantOsVolumes,
                },
                {
                    message: "Has non-compliant fixed volumes",
                    compliant: bitlockerStatus.haveNoncompliantFixedVolumes,
                },
                {
                    message: "Not backed up to management console",
                    compliant: bitlockerStatus.backupToMcCompliant,
                },
                {
                    message: "Has non-compliant startup authentication",
                    compliant: bitlockerStatus.startupTypeCompliant,
                }
            ].filter(e => !e.compliant).map(e => e.message);
            if (device.bitlocker_compliance == BitLockerCompliance.COMPLIANT && messages.length > 0) {
                const allMessages = messages.join(", ");
                const message = `${device.uuid}: bitlocker compliance: backend compliant but frontend not compliant because: ${allMessages}`;
                Sentry.captureMessage(message);
            } else if (messages.length == 0 && device.bitlocker_compliance == BitLockerCompliance.NON_COMPLIANT) {
                const message = `${device.uuid}: bitlocker compliance: backend non-compliant, but no frontend reason displayed.`;
                Sentry.captureMessage(message);
            }
        }, error => {
            console.error(`Failed to post log message: ${error}`);
        });
    }

    ngOnInit() {
        let som = localStorage.getItem('partitionsShowOnlyMounted');
        if (som != undefined) {
            this.showOnlyMounted = som == 'true';
        }

        this.showRansomware = authHelper.getFeatureFlagFromToken('ran') ?? true;
        if ( this.showRansomware ) {
            this.diskColumns.push('ransomware_detection')
            this.diskColumns.push('ransomware_sensitivity')
        }
        this.getConfig();

        this.restConfigService.getRestConfig().subscribe( response => {
          this.restConfig = response;
          this.getHomeDevice();
        });
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.stopTimer();
        document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    }

    isLocked() {
        return this.device?.state?.activeLock?.length > 0;
    }

    hasActiveLock() {
        let activeLocks = [] as number[];
        if (this.device.state && this.device.state.activeLock) {
            activeLocks = this.device.state.activeLock.filter(a => a !== this.CONSOLE_MANUAL_LOCK);
        }
        return activeLocks.length > 0;
    }

    hasManualLock() {
        return this.device.state?.activeLock?.filter(n => n === this.CONSOLE_MANUAL_LOCK).length > 0;
    }

    hasActiveLockDate(threatId): boolean {
        return this.deviceActiveLocks.some(d => d.sensorType === threatId);
    }

    getActiveLockDate(threatId: number): string {
        const activeLock = this.deviceActiveLocks.find(d => d.sensorType === threatId);
        return activeLock ? activeLock.timestamp : '';
    }

    getPbaAuthorization(): string {
        if (this.getPbaInfo(this.device)) {
            if(this.device.pbaAuthorization) {
                return "Authorized"
            }
            return "Unauthorized"
        }
        return "N/A - product not installed"
    }

    getFdeAuthorization(): string {
        if(this.getFdeVolume(this.device)) {
            if(this.device.fdeAuthorization) {
                return "Authorized"
            }
            return "Unauthorized"
        }

        return "N/A - product not installed"
    }

    getDataDefenseAuthorization(): string {
        //Not implemented currently
        return "N/A - product not installed"
    }

    getHomeDeviceActiveLocks() {
        this.route.params.subscribe(params => {
            this.d3eService.get_home_device_active_locks(params.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (result) => {
                  this.deviceActiveLocks = result.activeLocks;
                },
                error: () => {
                  this.router.navigateByUrl('/');
                }
              });
        });
    }

    getVolumeEncryption(volume: IVolume): string {
        return getEncryptionTypeDisplayFromVolume(volume, this.translate);
    }

    getProtector(protector: IVolumeProtection) : string {
        return getProtectorDisplayValue(protector, this.translate);
    }

    getVolumeTypeDisplay(volume: IVolume): string {
        if (volume?.volume_type != undefined) {
            switch (volume?.volume_type) {
                case VolumeType.OPERATING_SYSTEM_VOLUME:
                    return this.translate.instant('endpoints.partitions.volume_type_enum.operating_system_volume');
                case VolumeType.FIXED_DATA_VOLUME:
                    return this.translate.instant('endpoints.partitions.volume_type_enum.fixed_data_volume');
                case VolumeType.REMOVABLE_DATA_VOLUME:
                    return this.translate.instant('endpoints.partitions.volume_type_enum.removable_data_volume');
                case VolumeType.OTHER:
                default:
                    return this.translate.instant('endpoints.partitions.volume_type_enum.other');
            }
        }
        return '';
    };

    getVolumeFlagsDisplay(volume: IVolume): string {
        let o = [];
        if (volume.volume_encryption.encryption_flags != undefined ) {
            if ((volume.volume_encryption.encryption_flags & 0x1) > 0) {
                o.push( this.translate.instant('endpoints.partitions.volume_flags_enum.data_only_mode') );
            }
            if ((volume.volume_encryption.encryption_flags & 0x2) > 0) {
                o.push( this.translate.instant('endpoints.partitions.volume_flags_enum.may_wipe_free_space') );
            }
            if ((volume.volume_encryption.encryption_flags & 0x00010000 ) > 0) {
                o.push( this.translate.instant('endpoints.partitions.volume_flags_enum.may_wipe_free_space') );
            }
        }
        return o.join(", ")
    }

    getLockedDisplay(volume: IVolume): string {
        if ( volume.locked != undefined ) {
            switch(volume.locked) {
                case 0:
                    return this.translate.instant('endpoints.partitions.volume_locked_enum.unlocked');
                case 1:
                    return this.translate.instant('endpoints.partitions.volume_locked_enum.locked');
            }
        }
        return '';
    }

    getVolumeProtectionDisplay(volume: IVolume): string {
        if ( volume.protection != undefined ) {
            switch (volume.protection) {
                case VolumeProtection.UNPROTECTED:
                    return this.translate.instant('endpoints.partitions.volume_protection_enum.unprotected');
                case VolumeProtection.PROTECTED:
                    return this.translate.instant('endpoints.partitions.volume_protection_enum.protected');
                case VolumeProtection.UNKNOWN:
                    if (volume.locked) {
                        return this.translate.instant('endpoints.partitions.volume_protection_enum.protected');
                    } else {
                        return this.translate.instant('endpoints.partitions.volume_protection_enum.unknown');
                    }
                default:
                    return this.translate.instant('endpoints.partitions.volume_protection_enum.unknown');
            }
        }
        return '';
    }


    getVolumeStatusDisplay(volume: IVolume): string {
        if (volume.volume_encryption?.encrypt_status != undefined ) {
            switch (volume.volume_encryption.encrypt_status) {
                case VolumeStatus.FULLY_DECRYPTED:
                    return this.translate.instant('endpoints.partitions.volume_status_enum.fully_decrypted');
                case VolumeStatus.FULLY_ENCRYPTED:
                    return this.translate.instant('endpoints.partitions.volume_status_enum.fully_encrypted');
                case VolumeStatus.ENCRYPTION_IN_PROGRESS:
                    return this.translate.instant('endpoints.partitions.volume_status_enum.encryption_in_progress');
                case VolumeStatus.DECRYPTION_IN_PROGRESS:
                    return this.translate.instant('endpoints.partitions.volume_status_enum.decryption_in_progress');
                case VolumeStatus.ENCRYPTION_PAUSED:
                    return this.translate.instant('endpoints.partitions.volume_status_enum.encryption_paused');
                case VolumeStatus.DECRYPTION_PAUSED:
                    return this.translate.instant('endpoints.partitions.volume_status_enum.decryption_paused');
                default:
            }
        }
        return '';
    }

    getHomeDevice() {
        this.route.params.subscribe(params => {
            this.headerService.setTitle(this.title);
            this.headerService.setSubtitle('');
            this.headerService.setIcon('mdi-monitor');
            this.headerService.showUserFilter(false);
            this.headerService.showRefreshButton(true);
            this.refreshHomeDevice(params.id);
        });
    }

    refreshHomeDevice(uuid: string) {
        this.lastRefresh = null;
        this.d3eService.get_home_device(uuid)
          .pipe(
            map((device: IDevice) => replaceGuidsWithMountLetters(device)),
            untilDestroyed(this)
          )
          .subscribe({
            next: (device: IDevice) => {
              this.device = device as IDevice;
              this.device$.next(device);
              this.forcePolicyResetEnabled = device.force_policy_reset === 0;
              this.processVolumes();
              this.pbaSettings = this.getPbaSettings(device);
              this.fdeSettings = this.getFdeSettings(device);
              LicenseMethods.setLastSeen(device, dayjs().utc());
              this.setLockState();
              this.getHomeDeviceActiveLocks();
              this.processDeviceConfig();
              this.getEffectivePolicy(device.uuid);
              this.setRefreshRate();
              this.networkSensorEnabled = device?.state?.enabled;
              this.authModesDisplayOrderFiltered = this.authModesDisplayOrder.filter(mode => {
                  if (mode === AuthMode.AUTH_WIN_NATIVE) {
                      return LicenseMethods.compareVersion(device.version, MIN_AUTH_WIN_NATIVE_VERSION) >= 0;
                  } else if (mode === AuthMode.AUTH_FACE_DEPRECATED || mode === AuthMode.AUTH_FINGER_DEPRECATED) {
                      return LicenseMethods.compareVersion(device.version, MIN_AUTH_WIN_NATIVE_VERSION) < 0;
                  } else {
                      return true;
                  }
                  });
              },
            error: () => {
              this.router.navigateByUrl('/');
              this.clearing = false;
              this.setLastRefresh();
              this.cdr.detectChanges();
            },
            complete: () => {
              this.clearing = false;
              this.setLastRefresh();
              this.cdr.detectChanges();
            }
          });
    }

    setLastRefresh() {
        setTimeout(() => this.lastRefresh = new Date(), 600);
    }

    private processVolumes() {
        this.volumes = [];

        this.device.config?.disks?.forEach((element) => {
            if (element.volumes && element.volumes.length > 0) {
                element.volumes.forEach(volume => {
                    // tony puts in \u000 as a value, which trim does not see, the call to replace gets rid of it
                    if ((!this.showOnlyMounted) ||
                        (volume.mount && volume.mount.trim().replace('\0', '') !== '')) {

                        let new_volume = {...volume}

                        new_volume.diskSerial = element.serial;
                        new_volume.guid = new_volume.guid.replace('{','').replace('}', '');
                        if (volume.volume_encryption && volume.volume_encryption.protectors) {
                            let protectorDisplay = []
                            volume.volume_encryption.protectors.forEach(protector => {
                                if (protector.protector_type != undefined) {
                                    protectorDisplay.push(getProtectorDisplayValue(protector, this.translate));
                                }
                            });
                            new_volume.volume_encryption.protector_display = protectorDisplay.join(", ");
                        }
                        this.volumes.push(new_volume);
                    }
                });
            }
        });

    }

    getEffectivePolicy(uuid: string) {
        this.d3eService.get_effective_policy(uuid)
            .pipe(untilDestroyed(this))
            .subscribe(p => {
                this.bitLockerStatus$.next(new BitLockerStatus(this.device, p?.policy, this.translate))
                this.effectivePolicy = p;
            });
    }

    getAuthModeText(mode: AuthMode): string {
        let name: string;
        switch (mode) {
            case AuthMode.AUTH_PIN:
                // No translation for 'PIN'
                name = AuthModeName.AUTH_PIN;
                break;
            case AuthMode.AUTH_GOOGLE:
                name = this.fAuthApp;
                break;
            case AuthMode.AUTH_FINGER_DEPRECATED:
                name = this.fFingerprint;
                break;
            case AuthMode.AUTH_FACE_DEPRECATED:
                name = this.fFace;
                break;
            case AuthMode.AUTH_DUO:
                name = this.translate.instant('CISCO_DUO');
                break;
            case AuthMode.AUTH_PIV:
                // No translation for 'CAC/PIV'
                name = AuthModeName.AUTH_PIV;
                break;
            case AuthMode.AUTH_WIN_NATIVE:
                name = this.translate.instant('AUTH_WIN_NATIVE')
                break;
            default:
                name = this.fInvalid;
                break;
        }

        return name;
    }

    getAuthModeDescription(mode: AuthMode): string {
        let description: string;
        switch (mode) {
            case AuthMode.AUTH_PIN:
                description = this.fPinDescription;
                break;
            case AuthMode.AUTH_GOOGLE:
                description = this.fAuthAppDescription;
                break;
            case AuthMode.AUTH_FINGER_DEPRECATED:
                description = this.fFingerprintDescription;
                break;
            case AuthMode.AUTH_FACE_DEPRECATED:
                description = this.fFaceDescription;
                break;
            case AuthMode.AUTH_DUO:
                description = this.translate.instant('CISCO_DUO_DESCRIPTION');
                break;
            case AuthMode.AUTH_PIV:
                description = this.translate.instant('AMD_PIV_DESCRIPTION');
                break;
            case AuthMode.AUTH_WIN_NATIVE:
                description = this.translate.instant('AUTH_WIN_NATIVE_DESCRIPTION');
                break;
            default:
                description = this.fInvalidDescription;
                break;
        }

        return description;
    }

    getAuthModeState(mode: AuthMode) {
        return this.device &&
        this.device.state &&
        this.device.state.authenticationMode &&
        this.device.state.authenticationMode === mode ? this.fYes : this.fNo;
    }

    getAuthModeConfigured(mode: AuthMode) {
        return this.device &&
        this.device.config &&
        this.device.config.configuredAuthenticationModes &&
        this.device.config.configuredAuthenticationModes.indexOf(mode) >= 0 ? this.fYes : this.fNo;
    }

    getConfig() {
        this.d3eService
            .get_home_config()
            .pipe(
                catchError(() => of({} as IHomeConfig)),
                untilDestroyed(this)
            )
            .subscribe(
                config => {
                    this.homeConfig = config;

                    this.show_force_sync_btn = this.homeConfig.show_force_sync_btn ?? false;
                    this.disconnect_threshold = this.homeConfig.disconnect_threshold ?? this.disconnect_threshold;
                    this.refreshSeconds = this.homeConfig.device_page_refresh ?? this.refreshSeconds;

                    this.homeConfig.sensor_types.forEach(s => {
                        this.sensorsById[s.value.toString()] = s.description || s.displayValue;
                    });

                    if (this.configuredIntegrations.length > 0) {
                        // Add the 'Any' Integration that contains all of the integration threat id's
                        this.integrations.push(this.integrationMetadata[0]);
                        this.integrationMetadata.forEach(d => {
                            if (this.configuredIntegrations.includes(d.partnerId)) {
                                this.integrations.push(d);
                            }
                        });
                    }

                    if (!this.destroyed) {
                        this.setup_refresh();
                    }
                });
    }

    forceSyncConfig() {
        this.d3eService.force_sync_config_and_state(this.device.uuid)
            .subscribe(
                () => {
                    this.getHomeDevice();
                    this.notification.showSuccess(this.fSynchSuccess);
                },
                error => {
                    this.notification.showError(this.fErrorSyncing);
                }
            );
    }

    getOpalState(disk) {
        let opalEnabled = null;

        if ( disk.opal_state === 2 ) {
            return this.fCredsRequired;
        }

        if (disk.opal_state === 1) {
            opalEnabled = true;
        } else if (disk.opal_state === 0 || disk.opal_state === 4) {
            opalEnabled = false;
        }
        return opalEnabled === true ? this.fConfigured : opalEnabled === false ? this.fAvailable : this.fna;
    }

    getOpalStateText(disk: IDisk): string {
        return getFullOpalState(disk.opal_state, this.translate);
    }

    getTcgSupport(disk: IDisk): string {
        let tcgSupport = 'N/A';
        if ( disk.tcg_support !== undefined && disk.tcg_support.length > 0 ) {
            tcgSupport = '';
            for ( let i = 0; i < disk.tcg_support.length; i++ ) {
                if ( i > 1 ) {
                    tcgSupport += '/';
                }
                tcgSupport += disk.tcg_support[i];
            }
        }
        return tcgSupport;
    }

    getPBAInfo(disk: IDisk): string {
        let pbaInfo = this.translate.instant('unknown');
        if (disk?.pba_info) {
            if (disk.pba_info?.installed) {
                pbaInfo = `${this.translate.instant('device.pba.installed')} (v${disk.pba_info.version})`;
            } else {
                pbaInfo = `${this.translate.instant('device.pba.not-installed')} (v${disk.pba_info.version})`;
            }
        }
        return pbaInfo;
    }

    getSensitivity(disk: IDisk) {
        let sensitivity = 100;
        if ( disk?.ransomwareRawSensitivity ) {
            // Note: This algorithm is specific to the current AI Model, and may change with
                // different disk types.  It is also calculated in the back end in ctmc/drive_functions#get_ransomware_sensitivity
                // and in src/app/pages/secure-ssd/secure-ssd.component.ts
            sensitivity = 102 - disk.ransomwareRawSensitivity;
            if (sensitivity > 100) {
                sensitivity = 100;
            }
            if ( sensitivity < 1 ) {
                sensitivity = 1;
            }
        }
        return sensitivity;
    }

    isBitSet(disk: IDisk, flag: bigint): boolean {
        // tslint:disable-next-line:no-bitwise
        const val = BigInt(disk.support) & flag;
        return val !== 0n;
    }

    clearThreats() {
        this.clearing = true;
        forkJoin(this.device.state.activeLock.map(
            sensorNum => this.d3eService.post_threat(false, this.device.uuid, sensorNum)
          )
        ).subscribe(() => {
            this.notification.showSuccess(this.translate.instant('SUCCESS'));
            this.refreshHomeDevice(this.device.uuid);
        });
    }

    toggleConsoleLocked(checked: boolean) {
        this.d3eService.post_threat(checked, this.device.uuid)
            .subscribe();
        this.device.threats_pending = 1;
        this.setPendingList(this.CONSOLE_MANUAL_LOCK, checked);
        this.setLockState();
        this.interval$.next(2);
    }

    clearIntegrationAlarms(threatId: number) {
        if (!!this.device.state) {
            if (!!this.device.state.activeLock) {
                const integration = this.integrationMetadata.find(i => {
                    if (i.partnerId === -1 ) { return false; }
                    return i.threatIds.includes(threatId);
                });
                if (integration) {
                    this.device.state.activeLock.forEach(activeThreatId => {
                        if (integration.threatIds.includes(activeThreatId)) {
                            this.d3eService.post_threat(false, this.device.uuid, activeThreatId).subscribe();
                            this.device.threats_pending = 1;
                            this.setPendingList(activeThreatId, false);
                            this.setLockState();
                        }
                    });
                }
            }
        }
    }

    setPendingList(activeThreatId: number, enabled: boolean) {
        this.device.pending_list = this.device.pending_list || {pending: []} as IPendingList;
        const foundThreat = this.device.pending_list.pending.find(s => s.sensor_id === activeThreatId);

        if (foundThreat) {
            foundThreat.enabled = enabled;
        } else {
            this.device.pending_list.pending.push({
                'sensor_id': activeThreatId,
                'enabled': enabled
            });
        }
        this.device.threats_pending = 1;
    }

    setLockState() {
        // active_lock is a number. false if undefined or 0, true otherwise.
        const active_lock = !!this.device.active_lock;
        let console_engaged = active_lock && this.device.state.activeLock.includes(this.CONSOLE_MANUAL_LOCK) || false;

        // threats_pending is a number. false if undefined or 0, true otherwise.
        const threatsPending = !!this.device.threats_pending;
        const pendingConsoleItem = threatsPending &&
            this.device.pending_list?.pending.find(item => item.sensor_id === this.CONSOLE_MANUAL_LOCK);

        if (pendingConsoleItem) {
            console_engaged = !!pendingConsoleItem.enabled;
            this.consoleLockState = console_engaged ? LOCK_STATE_UNLOCKED_AND_PENDING : LOCK_STATE_LOCKED_AND_PENDING;
        } else {
            this.consoleLockState = console_engaged ? LOCK_STATE_LOCKED : LOCK_STATE_UNLOCKED;
        }

        this.device.toggle = console_engaged;
    }

    get activeLocks() {
        return this.device.state.activeLock.filter(v => v != this.CONSOLE_MANUAL_LOCK);
    }

    processDeviceConfig() {
        if (this.device && this.device.config) {
            this.device.opal_enabled = null;
            this.device.folder_enabled = false;
            this.device.locked_folders = 0;
            this.device.protected_folders = 0;
            this.device.deception_enabled = false;
            this.device.deception_files = 0;
            this.device.ext_enabled = false;
            this.device.locked_exts = 0;
            this.device.protected_exts = 0;
            this.device.volumes = 0;

            if (this.device.config.disks != null) {
                this.hasPbaSettings = this.device.config.disks.some(d => d?.pba_info?.settings);
                this.hasFde = this.device.config.disks.some(d => d?.volumes?.some(v => {
                     return v?.volume_encryption?.encryption === this.CIGENT_FDE;
                 }));
                for (const disk of this.device.config.disks) {
                    if (disk?.opal_state === 1 /*CIGENT_PROVISIONED */) {
                        this.device.opal_enabled = true;
                        this.device.volumes += disk?.volumes?.length ?? 0;
                    } else if (this.device.opal_enabled == null &&
                        (disk.opal_state === 0 || disk.opal_state === 4)) {
                        // only set this if opal_enabled has not yet been set to true
                        this.device.opal_enabled = false;
                    }
                }
                this.device.deception_enabled = this.device.config.deceptionFiles && this.device.config.deceptionFiles.length > 0;
                this.device.folder_enabled = this.device.config.lockedDirs && this.device.config.lockedDirs.length > 0;
                this.device.folder_enabled = this.device.config.protectedDirs && this.device.config.protectedDirs.length > 0;
                this.device.ext_enabled = this.device.config.lockedExts && this.device.config.lockedExts.length > 0;
                this.device.ext_enabled = this.device.config.protectedExts && this.device.config.protectedExts.length > 0;
            }
        }
    }

    isDeviceOpalEnabled(): boolean {
        let isEnabled = false;
        if (this.device.config && this.device.config.disks) {
            for (const disk of this.device.config.disks) {
                if (disk.opal_state === 1 /*CIGENT_PROVISIONED */) {
                    return true;
                } else if (this.device.opal_enabled == null &&
                    (disk.opal_state === 0 || disk.opal_state === 4)) {
                    // only set this if opal_enabled has not yet been set to true
                    isEnabled = false;
                }
            }
        }
        return isEnabled;
    }

    isNetworkDeceptionEnabled(): boolean {
        let isEnabled = false;
        if (this.device && this.device.config && this.device.config.deceptionUrls) {
            isEnabled = this.device.config.deceptionUrls.length > 0;
        }
        return isEnabled;
    }

    getThreatName(threatId: number) {
        if (threatId === this.CONSOLE_MANUAL_LOCK) {
            return this.fManualLockNoDash;
        }
        const integration = this.integrationMetadata.find(i => {
            if (i.partnerId === -1 ) { return false; }
            return i.threatIds.includes(threatId);
        });
        return integration ? integration.name : this.fUnknownThreat;
    }

    hasIntegrationAlarm(integration: IIntegration) {
        if (integration.partnerId === -1) {
            return false;
        }

        if (this.device.state) {
            const deviceState = this.device.state;
            if (typeof deviceState.activeLock !== 'undefined') {
                if (Array.isArray(deviceState.activeLock)) {
                    try {
                        // find if any of the thread ids for the integration are in the deviceState list
                        const found = integration.threatIds.some(r => deviceState.activeLock.includes(r));
                        if (found) {
                            return true;
                        }
                    } catch (e) {
                        console.error('Exception attempting to determine active lock', e);
                    }
                } else {
                    console.error('not an array');
                }
            }
        }

        return false;
    }

    private setRefreshRate() {
        if (this.consoleLockState === 1 || this.consoleLockState === 3) {
            if (this.interval$.getValue() !== this.refreshPendingSeconds) {
                this.interval$.next(this.refreshPendingSeconds);
            }
        } else {
            if (this.interval$.getValue() !== this.refreshSeconds) {
                this.interval$.next(this.refreshSeconds);
            }
        }
    }

    private setup_refresh() {
        this.startTimer();
        this.boundVisibilityHandler = this.handleVisibilityChange.bind(this);
        document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    }

    isDisconnected() {
        return this.device.last_seen_seconds == 0 || this.device.last_seen_seconds > this.disconnect_threshold;
    }

    getBannerClasses() {
        const disconnected = this.isDisconnected();
        const locked = this.isLocked();
        const standingGuard = !disconnected && !locked;
        return {
            "disconnected": disconnected,
            "locked": locked,
            "standing-guard": standingGuard,
        };
    }

    startTimer() {
        this.timerSubscription = this.interval$.pipe(
            switchMap(value => interval(value * 1000)),
            tap(() => {
                this.refreshHomeDevice(this.device.uuid);
            }),
            untilDestroyed(this)
        )
        .subscribe();
    }

    stopTimer() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = null;
        }
    }

    handleVisibilityChange() {
        if (document.hidden) {
            this.stopTimer();
        } else {
            this.startTimer();
        }
    }

    showAlertButton(id: number): boolean {
        return this.ALL_INTEGRATION_THREAT_IDS.includes(id) || this.THIRD_PARTY_THREAT_IDS.includes(id);
    }

    range(size: number, startAt: number = 0): ReadonlyArray<number> {
        return [...Array(size).keys()].map(i => i + startAt);
    }

    getDrive(disk: IDisk): IDrive {
        return toDriveInfo(disk);
    }

    forceResetPolicy() {
        this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
                title: this.translate.instant('force-policy-reset.confirmation-title'),
                content: this.translate.instant('force-policy-reset.confirmation-content'),
                rejectBtnName: this.translate.instant('NO'),
                acceptBtnName: this.translate.instant('YES'),
                showRejectBtn: true,
            },
        }).afterClosed().pipe(
          switchMap(result => {
              if (result) {
                  const licenses: IForcePolicyResetRequest = {'licenses': [this.device.uuid]};
                  return this.d3eService.force_policy_reset(licenses);
              }
              return EMPTY;
          }),
        ).subscribe({
            next: () => {
                this.getHomeDevice();
                this.notification.showSuccess(this.translate.instant('D3ED_FORCE_RESET_SUCCESS'));
            },
            error: () => {
                this.notification.showError(this.translate.instant('D3ED_FORCE_RESET_ERROR'));
            }
        })
    }

    displayDate(date) {
        return !date ? this.fNever : date;
    }

    @HostListener('window:keydown', ['$event'])
    showFeatureMask(event: KeyboardEvent) {
        this.ardbegBuffer.push(event.code);
        if (this.ardbegBuffer.length > this.ardbeg.length) {
            this.ardbegBuffer.shift();
        }
        if (this.ardbegBuffer.length === this.ardbeg.length) {
            if (this.ardbeg.every((k, i) => this.ardbegBuffer[i] === k)) {
                this.showFeatures = true;
            }
        }
    }

    updateDisplayedColumns(columns: string[]) {
        this.partitionColumnsToDisplay = columns;
    }

    changeShowOnlyMounted() {
        this.showOnlyMounted = !this.showOnlyMounted;
        localStorage.setItem('partitionsShowOnlyMounted', this.showOnlyMounted.toString());
        this.getHomeDevice();
    }

    protected readonly MIN_BITLOCKER_VERSION = MIN_BITLOCKER_VERSION;
    protected readonly MIN_BITLOCKER_WINDOWS_VERSION = MIN_BITLOCKER_WINDOWS_VERSION;
    protected readonly TPM_MIN_VERSION = TPM_MIN_VERSION;

    getReAuthFrequencyLabel(value: 0 | 1): string {
        return value === 1 ?
            this.translate.instant('policy.settings.reauth-frequency.fixed') :
            this.translate.instant('policy.settings.reauth-frequency.maximum');
    }

    getFeatureMask(value: ransomwareFeatureMaskValues): string {
        let featureMaskLabel: string;
        if (value === 1) {
            featureMaskLabel = this.translate.instant('policy.ransomware.feature-mask.report');
        } else if (value === 3) {
            featureMaskLabel = this.translate.instant('policy.ransomware.feature-mask.all');
        } else {
            featureMaskLabel = this.translate.instant('policy.ransomware.feature-mask.lock');
        }
        return featureMaskLabel;
    }

    getIntervalMinutes(interval: number): string {
        return this.getInterval(interval * 60 * 1000);
    }

    getInterval(interval: number): string {
        let unit = '';
        if (interval >= 0 && interval < 1000) {
            unit = this.fMillisecond;
        } else if (interval >= 1000 && interval < 60000) {
            interval = interval / 1000;
            unit = this.fSecond;
        } else if (interval >= 60000 && interval < 3600000) {
            interval = interval / 1000 / 60;
            unit = this.fMinute;
        } else if (interval >= 3600000 && interval < 86400000) {
            interval = interval / 1000 / 60 / 60;
            unit = this.fHour;
        } else if (interval >= 86400000 && interval < 604800000) {
            interval = interval / 1000 / 60 / 60 / 24;
            unit = this.fDay;
        } else if (interval >= 604800000) {
            interval = interval / 1000 / 60 / 60 / 24 / 7;
            unit = this.fWeek;
        }

        interval = Math.round(interval);
        const browserLanguage = localStorage.getItem('language');
        const asianLanguage = ['zh', 'zh-TW'].includes(browserLanguage);

        return interval < 0 || interval === 1 || asianLanguage ? `${interval} ${unit}`.trim() : `${interval} ${unit}s`;
    }

    getVolsByType(volumes: IVolume[]): Map<VolumeType, IVolume[]> {
        const volsByType: Map<VolumeType, IVolume[]> = new Map<VolumeType, IVolume[]>();
        volsByType.set(VolumeType.OPERATING_SYSTEM_VOLUME, []);
        volsByType.set(VolumeType.FIXED_DATA_VOLUME, []);
        volsByType.set(VolumeType.REMOVABLE_DATA_VOLUME, []);
        volsByType.set(VolumeType.OTHER, []);
        volumes.forEach(volume => {
            if (volume?.volume_encryption?.encryption == 'BitLocker') {
                volsByType.get(volume.volume_type).push(volume);
            }
        });
        return volsByType;
    }

    getPbaInfo(device: IDevice): IPBAInfo | null {
        return (
            device.config?.disks?.find(disk => disk.pba_info?.installed)?.pba_info ??
            null
        );
    }

    getFdeVolume(device: IDevice): IVolume | null {
        return (
            device.config?.disks
                ?.flatMap(disk => disk.volumes ?? [])
                .find(volume => volume.volume_encryption?.encryption === 'CigentFDE')
            ?? null
        );
    }

    getPbaSerial(device: IDevice): string | null {
        return (
            device.config?.disks?.find(disk => disk.pba_info?.installed)?.serial ??
            null
        );
    }

    getPbaLicenseInfo(device: IDevice,serial: string | null): IPbaLicenseInfo | null {
        if (!serial) {
            return null;
        }

        return (
            device.db_pba_license_info?.find(
                (license: IPbaLicenseInfo) => license.serial === serial
            ) ?? null
        );
    }

    getPbaMaintExpirationDisplay(license: IPbaLicenseInfo): string {
        if (!license.maintenance_end_dttm)
            return 'Invalid';
        if (license.maintenance_end_dttm === '0') {
            return 'Perpetual';
        }

        return new Date(license.maintenance_end_dttm).toLocaleDateString();
    }

    getPbaLicenseStatus(license: IPbaLicenseInfo): string {
        if (!license.maintenance_end_dttm) {
            return 'Invalid';
        }
        if(license.maintenance_end_dttm === '0') {
            return 'Active';
        }

        const expiration = new Date(license.maintenance_end_dttm);
        const now = new Date();

        if (expiration < now) {
            return 'Expired';
        }

        const threeMonthsFromNow = new Date(now);
        threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

        if (expiration <= threeMonthsFromNow) {
            return 'Expiring Soon';
        }

        return 'Active';
    }

    getFdeMaintExpirationDisplay(license: IFdeLicenseInfo): string {
        if (!license.maintenance_end_dttm)
            return 'Invalid';
        if (license.maintenance_end_dttm === '0') {
            return 'Perpetual';
        }

        return new Date(license.maintenance_end_dttm).toLocaleDateString();
    }

    getFdeLicenseStatus(license: IFdeLicenseInfo): string {
        if (!license.maintenance_end_dttm) {
            return 'Invalid';
        }
        if(license.maintenance_end_dttm === '0') {
            return 'Active';
        }

        const expiration = new Date(license.maintenance_end_dttm);
        const now = new Date();

        if (expiration < now) {
            return 'Expired';
        }

        const threeMonthsFromNow = new Date(now);
        threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

        if (expiration <= threeMonthsFromNow) {
            return 'Expiring Soon';
        }

        return 'Active';
    }

    isAgentInstalled(device: IDevice): boolean {
        return !!device.agent_version &&
            device.agent_version.trim() !== '' &&
            device.agent_version !== '0.0.0';
    }

    getPbaSettings(device: IDevice) {
        return device.config?.disks?.find(d => d?.pba_info?.settings)?.pba_info?.settings ?? null;
    }

    getFdeSettings(device: IDevice): IFdeSettings | null {
        return (
            device.config?.disks
                ?.flatMap(disk => disk.volumes ?? [])
                .find(volume => volume?.volume_encryption?.settings)
                ?.volume_encryption?.settings ?? null
        );
    }

    hasPbaEnrollmentCodes(device: IDevice): boolean {
        return (
            ((device.db_pba_enrollment ?? []).length > 0) ||
            ((device.db_pending_pba_enrollment ?? []).length > 0)
        );
    }

    hasFdeEnrollmentCodes(device: IDevice): boolean {
        return (
            ((device.db_fde_enrollment ?? []).length > 0) ||
            ((device.db_pending_fde_enrollment ?? []).length > 0)
        );
    }

    getPbaEnrollmentCodes(device: IDevice): IPbaEnrollment[] {
        return [
            ...(device.db_pending_pba_enrollment ?? []),
            ...(device.db_pba_enrollment ?? [])
        ];
    }

    getFdeEnrollmentCodes(device: IDevice): IFdeEnrollment[] {
        return [
            ...(device.db_pending_fde_enrollment ?? []),
            ...(device.db_fde_enrollment ?? [])
        ];
    }

    hasPbaUsers(device: IDevice): boolean {
        return (
            ((device.db_pending_pba_users ?? []).length > 0) ||
            ((device.db_pba_users ?? []).length > 0)
        );
    }

    hasFdeUsers(device: IDevice): boolean {
        return (
            ((device.db_pending_fde_users ?? []).length > 0) ||
            ((device.db_fde_users ?? []).length > 0)
        );
    }

    getPbaUsers(device: IDevice): IPbaUser[] {
        const seen = new Set<string>();
        const result: IPbaUser[] = [];

        const currentUsers = device.db_pba_users ?? [];
        const currentUserMap = new Map<string, IPbaUser>();
        for (const user of currentUsers) {
            currentUserMap.set(user.username, { ...user });
        }

        const pendingUsers = device.db_pending_pba_users ?? [];
        for (const pending of pendingUsers) {
            const username = pending.username;
            if (!username) continue;

            const baseUser = currentUserMap.get(username) ?? {
                username,
                email: '',
                role: 0,
                disk_serial: '',
            };

            const mergedUser: IPbaUser = {
                ...baseUser,
                ...pending,
            };

            if (!seen.has(username)) {
                seen.add(username);
                result.push(mergedUser);
            }
        }

        for (const [username, user] of currentUserMap) {
            if (!seen.has(username)) {
                seen.add(username);
                result.push(user);
            }
        }

        return result;
    }

    getFdeUsers(device: IDevice): IFdeUser[] {
        const seen = new Set<string>();
        const result: IFdeUser[] = [];

        const currentUsers = device.db_fde_users ?? [];
        const currentUserMap = new Map<string, IFdeUser>();
        for (const user of currentUsers) {
            currentUserMap.set(user.username, { ...user });
        }

        const pendingUsers = device.db_pending_fde_users ?? [];
        for (const pending of pendingUsers) {
            const username = pending.username;
            if (!username) continue;

            const baseUser = currentUserMap.get(username) ?? {
                username,
                email: '',
                role: 0,
                disk_serial: '',
            };

            const mergedUser: IFdeUser = {
                ...baseUser,
                ...pending,
            };

            if (!seen.has(username)) {
                seen.add(username);
                result.push(mergedUser);
            }
        }

        for (const [username, user] of currentUserMap) {
            if (!seen.has(username)) {
                seen.add(username);
                result.push(user);
            }
        }

        return result;
    }

    getPbaLicExpirationDisplay(license: IPbaLicenseInfo): string {
        if (!license.license_exp_dttm)
            return 'Perpetual';
        if (license.license_exp_dttm === '0') {
            return 'Perpetual';
        }

        return new Date(license.license_exp_dttm).toLocaleDateString();
    }

    getFdeLicExpirationDisplay(license: IPbaLicenseInfo): string {
        if (!license.license_exp_dttm)
            return 'Perpetual';
        if (license.license_exp_dttm === '0') {
            return 'Perpetual';
        }

        return new Date(license.license_exp_dttm).toLocaleDateString();
    }

    private getPbaComparisons(): [unknown, unknown][] {
        return [
            [
                this.effectivePolicy?.policy?.pbaSettings?.failedLoginsBeforeLockout,
                this.pbaSettings?.failed_logins_before_lockout
            ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.failedLoginsBeforeErase,
                this.pbaSettings?.failed_logins_before_erase
            ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.passwordHistory,
                this.pbaSettings?.password_history
            ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.passwordMin,
                this.pbaSettings?.password_min
            ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.mfa,
                this.pbaSettings?.mfa_required
            ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.mfaOptions,
                this.pbaSettings?.mfa_option
            ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.enableRememberMe,
                this.pbaSettings?.enable_remember_me
            ],
            // [
            //     this.effectivePolicy?.policy?.pbaSettings?.allowTPM,
            //     this.pbaSettings?.tpm_auto
            // ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.smartcardLoginEnabled,
                this.pbaSettings?.smartcard_login_enabled
            ],
            [
                this.effectivePolicy?.policy?.pbaSettings?.securityKeyLoginEnabled,
                this.pbaSettings?.security_key_login_enabled
            ]
        ];
    }

    getPbaPolicyCompliance(): PolicyCompliance {
        const statuses = this.getPbaComparisons().map(
            ([policyValue, endpointValue]) =>
                this.getPolicyCompliance(policyValue, endpointValue)
        );

        if (statuses.some(status => status === PolicyCompliance.UNKNOWN)) {
            return PolicyCompliance.UNKNOWN;
        }

        if (statuses.every(status => status === PolicyCompliance.COMPLIANT)) {
            return PolicyCompliance.COMPLIANT;
        }

        return PolicyCompliance.NON_COMPLIANT;
    }

    private getFdeComparisons(): [unknown, unknown][] {
        return [
            [
                this.effectivePolicy?.policy?.fdeSettings?.failedLoginsBeforeLockout,
                this.fdeSettings?.failed_logins_before_lockout
            ],
            [
                this.effectivePolicy?.policy?.fdeSettings?.failedLoginsBeforeErase,
                this.fdeSettings?.failed_logins_before_erase
            ],
            [
                this.effectivePolicy?.policy?.fdeSettings?.passwordHistory,
                this.fdeSettings?.password_history
            ],
            [
                this.effectivePolicy?.policy?.fdeSettings?.passwordMin,
                this.fdeSettings?.password_min
            ],
            [
                this.effectivePolicy?.policy?.fdeSettings?.enableRememberMe,
                this.fdeSettings?.enable_remember_me
            ]
        ];
    }

    getPolicyCompliance(
        policyValue: unknown,
        endpointValue: unknown
    ): PolicyCompliance {
        if (
            policyValue === null ||
            policyValue === undefined ||
            endpointValue === null ||
            endpointValue === undefined
        ) {
            return PolicyCompliance.UNKNOWN;
        }

        return policyValue === endpointValue
            ? PolicyCompliance.COMPLIANT
            : PolicyCompliance.NON_COMPLIANT;
    }

    getFdePolicyCompliance(): PolicyCompliance {
        const comparisons = this.getFdeComparisons();

        if (
            comparisons.some(
                ([policyValue, endpointValue]) =>
                    this.getPolicyCompliance(policyValue, endpointValue) ===
                    PolicyCompliance.UNKNOWN
            )
        ) {
            return PolicyCompliance.UNKNOWN;
        }

        return comparisons.every(
            ([policyValue, endpointValue]) =>
                this.getPolicyCompliance(policyValue, endpointValue) ===
                PolicyCompliance.COMPLIANT
        )
            ? PolicyCompliance.COMPLIANT
            : PolicyCompliance.NON_COMPLIANT;
    }

    isPerpetualExpiration(expiration: string | Date): boolean {
        if (expiration === '0' || expiration === '-1') {
            return true;
        }
        
        const date = new Date(expiration);
        return !isNaN(date.getTime()) && date.getTime() === 0;
    }

    protected readonly UserRole = UserRole;
    protected readonly VolumeType = VolumeType;
    protected readonly PolicyCompliance = PolicyCompliance;
    protected readonly ProductUserRole = ProductUserRole;
    protected readonly ConsoleAction = ConsoleAction;
}
